import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Megaphone,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Globe,
  Calendar,
  X,
  Check,
  Image,
  Type,
  Upload,
  Lock,
  Database,
  Download,
  RotateCcw,
  HardDrive
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = RUNTIME_BACKEND_URL;

export default function AdminSettingsPage() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Site ayarları
  const [siteTitle, setSiteTitle] = useState('WeddingTakip');
  const [logoUrl, setLogoUrl] = useState('');
  const [footerText, setFooterText] = useState('');
  
  // Duyurular
  const [announcements, setAnnouncements] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    is_active: true,
    expires_at: ''
  });

  // Şifre değişikliği
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Yedekleme
  const [backups, setBackups] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(null);

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Mevcut ve yeni şifre gerekli');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    setChangingPassword(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Şifre değiştirilemedi');
      }
      toast.success('Şifre başarıyla değiştirildi');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const loadBackups = async () => {
    try {
      const res = await fetch(`${API}/api/admin/backup/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setBackups(await res.json());
    } catch { /* silent */ }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const res = await fetch(`${API}/api/admin/backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Yedekleme başarısız');
      }
      const data = await res.json();
      toast.success(`Yedek oluşturuldu (${data.size_mb} MB)`);
      loadBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = (backup) => {
    window.open(`${API}${backup.download_url}?token=${token}`, '_blank');
  };

  const restoreBackup = async (backup) => {
    if (!window.confirm(`"${backup.filename}" yedeğini geri yüklemek istediğinize emin misiniz?\n\nDİKKAT: Mevcut tüm veriler bu yedekteki verilerle değiştirilecek!`)) return;
    setRestoringBackup(backup.filename);
    try {
      const res = await fetch(`${API}/api/admin/backup/restore/${backup.filename}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Geri yükleme başarısız');
      }
      toast.success('Veritabanı başarıyla geri yüklendi');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRestoringBackup(null);
    }
  };

  useEffect(() => {
    loadData();
    loadBackups();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Site ayarları
      const siteRes = await fetch(`${API}/api/admin/settings/site`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (siteRes.ok) {
        const data = await siteRes.json();
        setSiteTitle(data.site_title || 'WeddingTakip');
        setLogoUrl(data.logo_url || '');
        setFooterText(data.footer_text || '');
      }
      
      // Duyurular
      const annRes = await fetch(`${API}/api/admin/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (annRes.ok) {
        const data = await annRes.json();
        setAnnouncements(data);
      }
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSiteSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/settings/site`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          site_title: siteTitle,
          logo_url: logoUrl,
          footer_text: footerText 
        })
      });
      
      if (!res.ok) throw new Error('Kaydedilemedi');
      toast.success('Site ayarları kaydedildi');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim dosyaları yüklenebilir');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/admin/settings/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) throw new Error('Logo yüklenemedi');
      
      const data = await res.json();
      setLogoUrl(data.logo_url);
      toast.success('Logo yüklendi');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl('');
    toast.info('Logo kaldırıldı. Kaydetmeyi unutmayın.');
  };

  const handleOpenDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setAnnouncementForm({
        title: announcement.title,
        content: announcement.content,
        is_active: announcement.is_active,
        expires_at: announcement.expires_at?.split('T')[0] || ''
      });
    } else {
      setEditingAnnouncement(null);
      setAnnouncementForm({
        title: '',
        content: '',
        is_active: true,
        expires_at: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error('Başlık ve içerik gerekli');
      return;
    }

    setSaving(true);
    try {
      const body = {
        ...announcementForm,
        expires_at: announcementForm.expires_at ? new Date(announcementForm.expires_at).toISOString() : null
      };

      const url = editingAnnouncement 
        ? `${API}/api/admin/announcements/${editingAnnouncement.id}`
        : `${API}/api/admin/announcements`;
      
      const res = await fetch(url, {
        method: editingAnnouncement ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Kaydedilemedi');
      
      toast.success(editingAnnouncement ? 'Duyuru güncellendi' : 'Duyuru oluşturuldu');
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;

    try {
      const res = await fetch(`${API}/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Silinemedi');
      
      toast.success('Duyuru silindi');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <h1 className="text-2xl sm:text-3xl font-bold">Site Ayarları</h1>

      {/* Site Başlığı ve Logo */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5 text-primary" />
            Site Başlığı ve Logo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Site Başlığı */}
          <div className="space-y-2">
            <Label>Site Başlığı</Label>
            <Input
              value={siteTitle}
              onChange={(e) => setSiteTitle(e.target.value)}
              placeholder="Site başlığı (örn: WeddingTakip)"
              data-testid="site-title-input"
            />
            <p className="text-xs text-muted-foreground">
              Tarayıcı sekmesinde ve sol menüde görünecek isim.
            </p>
          </div>

          {/* Logo */}
          <div className="space-y-2">
            <Label>Site Logosu</Label>
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <div className="relative">
                  <img 
                    src={`${API}${logoUrl}`} 
                    alt="Site Logo" 
                    className="h-16 w-auto object-contain rounded border p-1"
                  />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="h-16 w-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                  <Image className="h-6 w-6" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {logoUrl ? 'Logo Değiştir' : 'Logo Yükle'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Önerilen boyut: 200x50 piksel, PNG veya SVG formatı.
            </p>
          </div>

          <Button onClick={handleSaveSiteSettings} disabled={saving} data-testid="save-site-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* Footer Ayarı */}
      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Site Alt Yazısı (Footer)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Alt Yazı Metni</Label>
            <Textarea
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              placeholder="Tüm fotoğrafçı sayfalarının altında görünecek metin..."
              rows={3}
              data-testid="footer-text-input"
            />
            <p className="text-xs text-muted-foreground">
              Bu metin tüm fotoğrafçı panellerinin alt kısmında görünecektir.
            </p>
          </div>
          <Button onClick={handleSaveSiteSettings} disabled={saving} data-testid="save-footer-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* Şifre Değişikliği */}
      <Card className="animate-fade-in stagger-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Şifre Değişikliği
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Mevcut Şifre</Label>
            <Input
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              placeholder="Mevcut şifreniz"
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Yeni Şifre</Label>
            <Input
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              placeholder="Yeni şifre (min 6 karakter)"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Yeni Şifre Tekrar</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Yeni şifreyi tekrar girin"
              data-testid="confirm-password-input"
            />
          </div>
          <Button onClick={handleChangePassword} disabled={changingPassword} data-testid="change-password-btn">
            {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            Şifreyi Değiştir
          </Button>
        </CardContent>
      </Card>

      {/* Duyurular */}
      <Card className="animate-fade-in stagger-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            Duyurular
          </CardTitle>
          <Button onClick={() => handleOpenDialog()} data-testid="add-announcement-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Duyuru
          </Button>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Henüz duyuru eklenmemiş.
            </p>
          ) : (
            <div className="space-y-3">
              {announcements.map((ann) => (
                <div 
                  key={ann.id} 
                  className={`p-4 rounded-lg border ${ann.is_active ? 'bg-primary/5 border-primary/20' : 'bg-muted/50 border-border'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{ann.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${ann.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                          {ann.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{ann.content}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Oluşturulma: {formatDate(ann.created_at)}</span>
                        {ann.expires_at && (
                          <span>Bitiş: {formatDate(ann.expires_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(ann)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteAnnouncement(ann.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duyuru Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Duyuruyu Düzenle' : 'Yeni Duyuru'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="Duyuru başlığı"
                data-testid="announcement-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>İçerik</Label>
              <Textarea
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                placeholder="Duyuru içeriği"
                rows={3}
                data-testid="announcement-content-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Bitiş Tarihi (Opsiyonel)</Label>
              <Input
                type="date"
                value={announcementForm.expires_at}
                onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Boş bırakılırsa duyuru manuel kapatılana kadar görünür.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktif</Label>
              <Switch
                checked={announcementForm.is_active}
                onCheckedChange={(checked) => setAnnouncementForm({...announcementForm, is_active: checked})}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveAnnouncement} disabled={saving} data-testid="save-announcement-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Veritabanı Yedekleme */}
      <Card className="animate-fade-in stagger-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Veritabanı Yedekleme
          </CardTitle>
          <Button onClick={createBackup} disabled={creatingBackup} data-testid="create-backup-btn">
            {creatingBackup ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <HardDrive className="h-4 w-4 mr-2" />
            )}
            {creatingBackup ? 'Yedekleniyor...' : 'Yeni Yedek Al'}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Tüm müşteri, fotoğrafçı, salon bilgileri, albümler ve ayarlar yedeklenir. Fotoğraf dosyaları ayrıca yedeklenmelidir.
          </p>

          {backups.length === 0 ? (
            <div className="text-center py-8">
              <Database className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-muted-foreground text-sm">Henüz yedek yok</p>
            </div>
          ) : (
            <div className="space-y-2">
              {backups.map((backup) => (
                <div
                  key={backup.filename}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <HardDrive className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{backup.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(backup.created_at).toLocaleDateString('tr-TR', {
                          day: 'numeric', month: 'long', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })} - {backup.size_mb} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => {
                        window.open(`${API}${backup.download_url}?token=${token}`, '_blank');
                      }}
                      data-testid={`download-backup-${backup.filename}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      İndir
                    </Button>
                    <Button
                      variant="destructive" size="sm"
                      onClick={() => restoreBackup(backup)}
                      disabled={restoringBackup === backup.filename}
                    >
                      {restoringBackup === backup.filename ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-1" />
                      )}
                      Geri Yükle
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
